'use client';

import Link from 'next/link';
import { cn } from '@/shared/lib/cn';
import type { EstimateSummary } from '../api/estimate-service';
import { EstimateDocumentView } from './EstimateDocumentView';
import { EstimateExportMenu } from './EstimateExportMenu';

interface EstimateCardProps {
  estimate: EstimateSummary;
  projectId: string;
  /** Concept the estimate belongs to. Falls back to the estimate's own
   *  `conceptId` (project-scoped list) when not passed (concept-scoped list). */
  conceptId?: string;
  /** Carousel layout = fixed-width card; list layout = full-width grid cell. */
  variant?: 'carousel' | 'list';
  /**
   * Same-page open. When provided (concept-page carousel) the card flips the
   * concept layout to the Estimate tab in place — parity with Apple's
   * `layoutState.activeTab = .estimate`. When absent (project Estimates page)
   * the card is a Link that navigates to the owning concept's Estimate tab.
   */
  onOpen?: () => void;
}

/**
 * Reusable estimate card. Shown in the project-page and concept-page Estimates
 * carousels and the dedicated project Estimates page. The visual is the
 * stylized estimate "document" (no photo) — see `EstimateDocumentView`.
 * Tapping opens the owning concept's Estimate tab (parity with how an album
 * card opens `?tab=album`).
 */
export function EstimateCard({
  estimate,
  projectId,
  conceptId,
  variant = 'carousel',
  onOpen,
}: EstimateCardProps) {
  const targetConceptId = conceptId ?? estimate.conceptId;
  const href = `/project/${projectId}/concept/${targetConceptId}?tab=estimate`;

  const isCarousel = variant === 'carousel';

  // Carousel cards match the album card footprint (156×208 ≈ panel height − 24
  // at 4:3); list cells fill their grid column at the same aspect.
  const className = cn(
    'group block aspect-[4/3]',
    isCarousel ? 'w-52 flex-shrink-0' : 'w-full'
  );

  const inner = (
    <EstimateDocumentView
      materialsTotal={estimate.materials_total}
      laborTotal={estimate.labor_total}
      grandTotal={estimate.grand_total}
      currency={estimate.currency}
      conceptNumber={estimate.conceptSequenceNumber}
      createdAt={estimate.created_at}
      status={estimate.status}
    />
  );

  // Export overlay — only for a ready estimate (export is invalid before
  // generation finishes). Rendered as a sibling of the link/button (not nested
  // inside it) so we never put an interactive trigger inside an anchor/button;
  // an absolute wrapper positions it over the document corner.
  const exportOverlay = estimate.status === 'ready' && (
    <div className="absolute start-2 top-2 z-10">
      <EstimateExportMenu estimateId={estimate.id} stopPropagation />
    </div>
  );

  // `pb-7` mirrors AlbumCard: the document paints its own drop shadow, and the
  // carousel's `overflow-x-auto` would chop the soft tail without a cushion.
  const wrapperClassName = cn(
    'relative pb-7',
    isCarousel ? 'flex-shrink-0' : 'w-full'
  );

  return (
    <div className={wrapperClassName}>
      {onOpen ? (
        <button type="button" onClick={onOpen} className={cn(className, 'text-start')}>
          {inner}
        </button>
      ) : (
        <Link href={href} className={className}>
          {inner}
        </Link>
      )}
      {exportOverlay}
    </div>
  );
}
